/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { If, Then, Else } from "react-if";
import { useParams } from "react-router-dom";
import { format } from "timeago.js";
import {
  Box,
  Button,
  Col,
  ModalConfirm,
  Row,
  SVGIcon,
  Tab,
  Tabs,
  Text,
} from "@nordcloud/gnui";
import {
  useExternalIntegrationDetailsQuery,
  useRegenerateExternalIntegrationSecretMutation,
} from "~/generated/graphql";
import { BreadcrumbsBox, Details, IconBox, LoaderWrap } from "~/components";
import { dateFormat, ERROR_TEXT } from "~/constants";
import {
  usePerson,
  useTabsWithUrl,
  useEstateFetch,
  useEstateRecords,
} from "~/hooks";
import { showError } from "~/services/toast";
import { isEmpty, noop } from "~/tools";
import {
  disabledFilterFields,
  DisabledFilters,
} from "~/views/estate/components/EstateFilters";
import { EstateRecords } from "~/views/estate/EstateRecords";
import { Cost } from "./components/Cost";
import { ReadOnlyField } from "./components/ReadOnlyField";

type RouteParams = {
  integrationId: string;
};

export function ExternalIntegrationsDetailsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiCredentials, setApiCredentials] = useState("");
  const { integrationId = "" } = useParams<RouteParams>();

  const { activeTab, setActiveTab } = useTabsWithUrl();

  const { result, ...filters } = useEstateFetch({
    defaultFilters: {
      resourceGroup: [integrationId],
    },
    skipFields: disabledFilterFields.get(DisabledFilters.EXTERNAL),
  });

  const { result: enrichedEstateResult } = useEstateFetch({
    defaultFilters: {
      enrichment: true,
      enrichmentIntegrationId: [integrationId],
    },
    skipFields: disabledFilterFields.get(DisabledFilters.ENRICHMENT),
  });

  const [
    regenerateExternalIntegrationSecret,
    { data: regenerateData, loading: isRegenerateLoading },
  ] = useRegenerateExternalIntegrationSecretMutation();

  // Temporary used for getting currentMonthTotal cost.
  const { data: costData } = useEstateRecords(
    {
      variables: {
        limit: 1,
        page: 0,
        query: "",
        filter: { externalIntegration: [integrationId] },
      },
    },
    noop
  );

  const { data: detailsData, loading: isLoadingDetails } =
    useExternalIntegrationDetailsQuery({
      variables: {
        integrationId,
      },
    });

  const { person } = usePerson(
    detailsData?.externalIntegrationDetails?.ownerId
  );

  const details = [
    {
      label: "MCA ID:",
      value: detailsData?.externalIntegrationDetails?.id,
      clipboard: true,
    },
    {
      label: "Integration Name:", // Change to editable component after update mutation was created
      value: detailsData?.externalIntegrationDetails?.name,
      clipboard: true,
    },
    {
      label: "Integration Owner:", // Change to editable component after update mutation was created
      value: person?.name ?? detailsData?.externalIntegrationDetails?.ownerId,
      clipboard: true,
    },
    {
      label: "Description:", // Change to editable component after update mutation was created
      value: detailsData?.externalIntegrationDetails?.description,
      clipboard: detailsData?.externalIntegrationDetails?.description !== "",
    },
    {
      label: "Creation date:",
      value: dayjs(
        detailsData?.externalIntegrationDetails?.createdAt ?? ""
      ).format(`${dateFormat.dayMonthYear}`),
      clipboard: false,
    },
    {
      label: "Last sync:",
      value: detailsData?.externalIntegrationDetails?.lastSync
        ? format(detailsData?.externalIntegrationDetails?.lastSync)
        : "N/A",
      clipboard: false,
    },
  ];

  const integrationName =
    detailsData?.externalIntegrationDetails?.name ?? integrationId;

  const breadcrumbLabels = [
    {
      value: "external-integrations",
      label: "External Integrations",
    },
    {
      value: integrationId,
      label: integrationName,
    },
  ];

  async function generateApiCredentialsHandler() {
    try {
      await regenerateExternalIntegrationSecret({
        variables: {
          integrationId,
        },
      });
    } catch {
      showError(ERROR_TEXT.default);
    }
  }

  useEffect(() => {
    setApiCredentials(
      regenerateData?.regenerateExternalIntegrationSecret?.secret ?? ""
    );
  }, [regenerateData]);

  function onModalClose() {
    setIsModalOpen(false);
    setApiCredentials("");
  }

  const isApiCredentialsEmpty = isEmpty(apiCredentials);

  return (
    <>
      <Row>
        <Col>
          <BreadcrumbsBox title={integrationName} labels={breadcrumbLabels}>
            <Button onClick={() => setIsModalOpen(true)}>
              Generate API credentials
            </Button>
          </BreadcrumbsBox>
          <Box spacing="spacing04">
            <Row>
              <Col xs={12} md={1}>
                <IconBox
                  icon={<SVGIcon name="externalIntegration" size="xxl" />}
                />
              </Col>
              <Col xs={12} md={4}>
                <Text isTitle weight="medium">
                  Integration Details
                </Text>
                <LoaderWrap inContent loading={isLoadingDetails}>
                  <Details details={details} />
                </LoaderWrap>
              </Col>
              <Col xs={12} md={3}>
                <Cost currentMonth={costData?.cost?.currentMonthTotal ?? "0"} />
              </Col>
            </Row>
          </Box>
        </Col>
      </Row>
      <Tabs step={activeTab} handleTab={setActiveTab}>
        <Tab heading="Added" caption="Estate records added by integration">
          <EstateRecords
            excludeColumns={["region"]}
            queryFilters={filters}
            dataEstate={result}
            disabledFilters={[DisabledFilters.EXTERNAL]}
          />
        </Tab>
        <Tab
          heading="Enriched"
          caption="Estate records enriched by integration"
        >
          <EstateRecords
            dataEstate={enrichedEstateResult}
            queryFilters={filters}
            disabledFilters={[DisabledFilters.ENRICHMENT]}
          />
        </Tab>
      </Tabs>
      <ModalConfirm
        isOpen={isModalOpen}
        contentLabel="Generate API Credentials"
        confirm={
          isApiCredentialsEmpty ? generateApiCredentialsHandler : onModalClose
        }
        actionLabel={
          isApiCredentialsEmpty ? "Generate API Credentials" : "Close"
        }
        onClose={onModalClose}
      >
        <LoaderWrap inContent loading={isRegenerateLoading}>
          <If condition={isApiCredentialsEmpty}>
            <Then>
              <Text>
                You can generate new API credentials here. After the new API
                credentials is generated, your original API credentials will no
                longer be available.
              </Text>
              <Text>
                Are you sure you want to generate new API credentials?
              </Text>
            </Then>
            <Else>
              <ReadOnlyField label="Secret" value={apiCredentials} />
            </Else>
          </If>
        </LoaderWrap>
      </ModalConfirm>
    </>
  );
}
